import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Field,
  Input,
  MessageBar,
  Spinner,
} from '@fluentui/react-components';
import {
  uploadFilingHistoryImport,
  listFilingHistoryImports,
} from '../../services/api';
import type { FilingImportSummary } from '../../types';

export default function FilingHistoryImportPage() {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [taxYear, setTaxYear] = useState<string>('2024');
  const [summary, setSummary] = useState<FilingImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const importsQuery = useQuery({
    queryKey: ['filing-history-imports'],
    queryFn: listFilingHistoryImports,
  });

  const uploadMutation = useMutation({
    mutationFn: ({ f, year }: { f: File; year: number }) => uploadFilingHistoryImport(f, year),
    onSuccess: (s) => {
      setSummary(s);
      setError(null);
      qc.invalidateQueries({ queryKey: ['filing-history-imports'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div style={{ padding: 24 }}>
      <h1>Filing History Import (Admin)</h1>
      <p>One-time backfill of 2023 / 2024 tax software exports.</p>

      {error && <MessageBar intent="error">{error}</MessageBar>}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', margin: '16px 0' }}>
        <Field label="Tax Year">
          <Input
            type="number"
            value={taxYear}
            onChange={(_, d) => setTaxYear(d.value)}
            aria-label="Tax Year"
          />
        </Field>
        <Field label="XLSX file">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            aria-label="XLSX file"
          />
        </Field>
        <Button
          appearance="primary"
          disabled={!file || uploadMutation.isPending}
          onClick={() => {
            if (file) uploadMutation.mutate({ f: file, year: parseInt(taxYear, 10) });
          }}
        >
          Upload
        </Button>
      </div>

      {uploadMutation.isPending && <Spinner label="Importing..." />}

      {summary && (
        <div style={{ marginTop: 16, padding: 16, background: '#f3f3f3', borderRadius: 4 }}>
          <h2>Import summary</h2>
          <ul>
            <li>Batch: {summary.importBatchId}</li>
            <li>Rows total: {summary.rowsTotal}</li>
            <li>Imported: {summary.rowsImported}</li>
            <li>Auto-matched: {summary.autoMatched}</li>
            <li>Confirmed-preserved: {summary.confirmed}</li>
            <li>Pending review: {summary.pending}</li>
            <li>Classified as review: {summary.classifiedAsReview}</li>
            <li>Errors: {summary.errors.length}</li>
          </ul>
          {summary.errors.length > 0 && (
            <details>
              <summary>Errors ({summary.errors.length})</summary>
              <ul>
                {summary.errors.map((e, i) => (
                  <li key={i}>Row {e.rowNumber} &mdash; {e.field}: {e.message}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <h2 style={{ marginTop: 32 }}>Past imports</h2>
      {importsQuery.isLoading && <Spinner />}
      {importsQuery.data && (
        <table>
          <thead>
            <tr><th>Batch</th><th>Tax Year</th><th>Rows</th><th>Pending</th><th>Confirmed</th><th>Auto</th></tr>
          </thead>
          <tbody>
            {importsQuery.data.map(b => (
              <tr key={b.importBatchId}>
                <td>{b.importBatchId}</td>
                <td>{b.taxYear}</td>
                <td>{b.rowCount}</td>
                <td>{b.pending}</td>
                <td>{b.confirmed}</td>
                <td>{b.auto}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
